/**
 * Shared runtime wiring for the generated @hey-api/openapi-ts connectors.
 *
 * Each connector (notifications, dataManagement, ...) gets its own generated
 * `client` instance and a thin per-connector module (e.g. notificationsApi.ts)
 * that sets its base URL and registers interceptors. The cross-cutting pieces
 * are identical across connectors and live here so they are defined once:
 *
 *  - auth: the Cognito id_token is the bearer for every backend;
 *  - 401 handling: logout only when the local session token is actually dead
 *    (missing, expired, or undecodable). A 401 while the local token is still
 *    unexpired is a service-side auth failure, not a dead session — report it
 *    and leave the session alone;
 *  - error normalization: with `throwOnError` the client rejects with the
 *    parsed error body, not an Error; callers expect an Error with a message.
 *
 * These are plain callbacks rather than a `configure(client)` helper on purpose:
 * each connector's generated `Client` type is a distinct (structurally
 * identical) type, so passing the client in would couple this module to one
 * connector's generated output. Wiring modules call client.setConfig /
 * client.interceptors.*.use themselves and pass these in.
 */
import jwtDecode, { type JwtPayload } from 'jwt-decode';
import { getToken } from '@bettercomp/ams-spa-auth';
import { getAuthCookie } from 'utils/auth';
import { reportError } from 'utils/reporting';
import { logout } from 'service/cognitoService';
import { isAmsAuthActive } from 'service/amsSession';
import { captureApiError } from 'utils/supportContext/errorBuffer';

/**
 * Per-request bearer token. The generated runtime calls this for every SDK
 * function whose operation carries `security` metadata, adds the `Bearer `
 * prefix itself, and omits the header when the token is undefined. A live AMS
 * session (in-memory access token) supersedes the legacy cookie; the two
 * artifacts are mutually exclusive by construction.
 */
export const connectorAuth = (): string | undefined => getToken() ?? getAuthCookie()?.id_token;

/**
 * Request interceptor that attaches the Cognito bearer to every request,
 * unconditionally. hey-api's `auth` option only fires for operations whose
 * OpenAPI spec declares `security`; connectors whose spec declares none
 * (DataHub / quart-schema emits no security scheme) would otherwise send no
 * Authorization header and get a 401. Mirrors the legacy connector, which
 * always sent the token. FastAPI connectors keep using `auth` instead.
 */
export const authorizeRequest = (request: Request): Request => {
  const token = connectorAuth();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
};

/**
 * Whether the id_token every connector sends (all read the same authTokens
 * cookie) is still unexpired by the client clock. Missing or undecodable
 * tokens count as dead: whatever we would keep sending can never authenticate.
 */
const isSessionTokenLive = (): boolean => {
  const token = getAuthCookie()?.id_token;
  if (!token) {
    return false;
  }
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

/**
 * Response interceptor factory: on a 401, log out only if the local session
 * token is actually dead (missing/expired/undecodable — the one legitimate
 * "session is gone" signal we can verify without trusting the service).
 *
 * A 401 while the local token is unexpired is a service-side auth failure —
 * JWKS fetch outage, issuer config drift, claim-shape mismatch — not a dead
 * session. `logout()` is a multi-tab session kill, so honoring such a 401
 * would boot every user out of the whole app in a loop until the service is
 * fixed; instead it is reported and the session left alone. The response is
 * returned unchanged. `apiName` only labels the report.
 */
export const createUnauthorizedInterceptor =
  (apiName: string) =>
  (response: Response): Response => {
    if (response.status !== 401 || typeof window === 'undefined') {
      return response;
    }
    // On the AMS stack the 401 already went through fetchWithRenewal's
    // renew-then-retry, and session death reaches the auth gate as a published
    // outcome — the cookie-expiry heuristic below is legacy-only.
    if (isAmsAuthActive()) {
      return response;
    }
    if (isSessionTokenLive()) {
      reportError(`${apiName} returned 401 with an unexpired session token; leaving the session intact`);
      return response;
    }
    reportError(`${apiName} returned 401 and the session token is dead; triggering auto-logout`);
    logout();
    return response;
  };

/**
 * Response interceptor factory that records non-2xx responses in the in-app
 * support request's error buffer: method, path and status only — never the body,
 * which carries compensation data and PII, and this content leaves the platform
 * in an email to support.
 *
 * Lives here rather than in each connector module because the buffer is one
 * shared 5-entry ring; duplicating the capture logic per connector would be two
 * copies of the same rule. Connectors opt in by registering it, so a background
 * poller cannot quietly evict the failures the user actually saw — which is why
 * notificationsApi deliberately does not register it.
 */
export const captureApiErrorInterceptor = (response: Response, request: Request): Response => {
  if (!response.ok) {
    captureApiError({ method: request.method, url: request.url, status: response.status });
  }
  return response;
};

/**
 * Error interceptor for the FastAPI connectors (notifications, dataManagement):
 * on a non-2xx with `throwOnError` the client rejects with the parsed error body
 * (FastAPI's `{ detail }`) or raw text, not an Error. Normalize to a real Error
 * so callers can rely on `err instanceof Error` and a readable `err.message`.
 */
export const normalizeFastApiError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    const { detail } = error as { detail: unknown };
    return new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return new Error(typeof error === 'string' ? error : JSON.stringify(error));
};

/**
 * Error interceptor for the DataHub connector. DataHub is a Quart/quart-schema
 * service whose error bodies are `{ message }` — its custom exception and
 * validation handlers, and every route, emit `message` (never FastAPI's
 * `{ detail }`). Normalize to a real Error carrying that message, matching what
 * the legacy DataHub connector surfaced.
 */
export const normalizeDataHubApiError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message } = error as { message: unknown };
    return new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return new Error(typeof error === 'string' ? error : JSON.stringify(error));
};
